'use client';

import React, { FC, useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'next/navigation';

import { useFlags } from 'launchdarkly-react-client-sdk';

import { DynamicComponent, TrustedShopReviews } from '@/components';
import Heading, {
  HeadingTag,
  HeadingSize,
} from '@/components/ui/Heading/Heading';
import { YotpoReviewStars } from '@/components/ui/YotpoReviewStars';

import useProductContext from '@/context/ProductContext';

import { ITEM_ORIGINS } from '@/core/constants';

import { TypeProduct, TypeShipmentList } from '@/core/generated-types';

import { API_URLS } from '@/lib/api/config';

import {
  ShopifyProduct,
  ShopifyVariant,
  getProductInventoryStatus,
} from '@/lib/shopify';

import { usePathname } from '@/navigation';

import { captureProductFieldsEvent, pushEvent } from '@/utils/dataLayer';

import {
  shouldUseTrustedShopsReviews,
  shouldUseYotpoReviews,
} from '@/utils/helpers';

import {
  PRODUCT_ADDED_COMPONENT_TYPES,
  PRODUCT_EVENT_TYPES,
} from '@/utils/rudderstack';

import { getDefaultVariant } from '@/utils/shopify';
import { handleCartFormSubmit } from '@/utils/shopify/handleCartFormSubmit';

import { Translations } from '@/utils/translations/generateTranslationObject';

import { AddButton, InventoryStatus } from '../InventoryStatus';

import ProductOptionsAddToCartTabs from './ProductOptionsAddToCartTabs';

import { ProductOptionsRadio } from '.';
import { TypeSubscriptionPlanInstallmentsResponse } from '@/lib/api/subscriptionManager/types';
import { buildPrimaryProductProperties } from '@/components/common/ProductAddToCart/utils';
import ProductValueProps from '@/components/common/ProductAddToCart/ProductValueProps';
import { TariffPDPMessage } from '../Tariff';
import { TypeTariffSettings } from '../types';
import AddToCartErrorMessage from '../AddToCartErrorMessage/AddToCartErrorMessage';
import useLocale from '@/hooks/useLocale';

export interface ProductOptionsAddToCartProps {
  products: TypeProduct[];
  shopifyProducts: ShopifyProduct[];
  alternativeProduct?: TypeProduct;
  shopifyAlternativeProduct?: ShopifyProduct;
  t: Translations;
  shipmentList?: TypeShipmentList;
  subscriptionPlanInstallments?: TypeSubscriptionPlanInstallmentsResponse;
  tariffSettings?: TypeTariffSettings;
  additionalSavingsText?: string;
  /** When true (e.g. when evergreenPromoText is set), do not render ProductValueProps to avoid duplicate checkbox-style content */
  hideProductValueProps?: boolean;
}

const ProductOptionsAddToCart: FC<ProductOptionsAddToCartProps> = ({
  products,
  shopifyProducts,
  alternativeProduct,
  shopifyAlternativeProduct,
  t,
  shipmentList,
  subscriptionPlanInstallments,
  tariffSettings,
  additionalSavingsText,
  hideProductValueProps = false,
}) => {
  const pathname = usePathname();

  const searchParams = useSearchParams();

  const locale = useLocale();

  const variantUrlParams = searchParams?.get('variant');

  const { enableYotpoReviewsSummary, enableTrustedShopsReviewsSummary } =
    useProductContext();

  // The component has a default option selected on the page load,
  // so the first index is automatically used as the main product
  const product = products[0];
  const shopifyProduct = shopifyProducts[0];

  const { 'sub-squad-enable-tabs-on-rss-pdp': enableTabsFlag } = useFlags();

  const [productSelected, setProductSelected] = useState(product);

  const [shopifyProductSelected, setShopifyProductSelected] =
    useState(shopifyProduct);

  const productFields = product.fields;

  const { variants } = shopifyProductSelected as ShopifyProduct;

  const defaultVariant = getDefaultVariant(productFields.title, variants);

  const [variant, setVariant] = useState<ShopifyVariant>(defaultVariant);
  const [addToCartError, setAddToCartError] = useState<string | null>(null);

  const qtyVal = 1;

  const handleOptionSelected = (
    product: TypeProduct,
    shopifyProduct: ShopifyProduct,
    variant: ShopifyVariant,
  ) => {
    setProductSelected(product);
    setShopifyProductSelected(shopifyProduct);
    setVariant(variant);
  };

  useEffect(() => {
    if (shopifyProduct) {
      const urlVariant: ShopifyVariant | undefined = variants?.find(
        (variant) =>
          `gid://shopify/ProductVariant/${variantUrlParams ?? ''}` ==
          variant.id,
      );

      if (urlVariant) {
        setVariant(urlVariant);

        pushEvent('productView', {
          qtyVal,
          shopifyProduct,
          variant: urlVariant,
        });

        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct,
          variant: urlVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });

        return;
      }

      // no variant selected, use the default selected variant
      if (variants && variants.length) {
        setVariant(defaultVariant);

        pushEvent('productView', {
          qtyVal: 1,
          shopifyProduct: shopifyProductSelected,
          variant: defaultVariant,
        });

        captureProductFieldsEvent(PRODUCT_EVENT_TYPES.PRODUCT_VIEWED, {
          shopifyProduct: shopifyProductSelected,
          variant: defaultVariant,
          product: productFields,
          quantity: qtyVal,
          url: pathname || '',
        });

        return;
      }

      console.warn('Product is missing variants');
    }
    // Removed dependency array to avoid triggering multiple events
    // when component is re-rendered due to options selection
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const inventoryStatus = getProductInventoryStatus(
    shopifyProductSelected,
    variant,
  );

  function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    setAddToCartError(null);
    handleCartFormSubmit({
      e,
      shopifyProduct: shopifyProductSelected,
      shopifyVariant: variant,
      productFields: productSelected.fields,
      url: pathname,
      defaultQuantity: qtyVal,
      buttonLevel: PRODUCT_ADDED_COMPONENT_TYPES.PRIMARY_CTA,
      onSubmitFailure: () => {
        e.preventDefault();
        setAddToCartError(
          "This item wasn't added to your cart. Please try again.",
        );
      },
      locale,
    }).catch(console.error);
  }

  const OriginalProductOptions = useMemo(
    () => (
      <div className="flex flex-col ">
        <ProductOptionsRadio
          products={products}
          productSelected={productSelected}
          shopifyProducts={shopifyProducts}
          onChange={handleOptionSelected}
          originalProduct={product}
        />

        {shopifyProductSelected && (
          <form
            id="add-to-cart-form"
            data-testid="add-to-cart-form"
            action={`${API_URLS.CART_SERVICE}/add`}
            method="post"
            onSubmit={onSubmit}
          >
            <input
              type="hidden"
              name="lines"
              value={JSON.stringify([
                {
                  merchandiseId: variant.id,
                  quantity: qtyVal,
                  attributes: buildPrimaryProductProperties(
                    shopifyProductSelected,
                    ITEM_ORIGINS.PRODUCT_PAGE,
                    subscriptionPlanInstallments?.data?.starting_installments,
                    productSelected.fields.subscriptionPlanAnchor,
                  ),
                },
              ])}
            />

            <div className="text-sm mb-2">
              <InventoryStatus
                inventoryStatus={inventoryStatus}
                product={product}
              />
            </div>

            <AddButton inventoryStatus={inventoryStatus} product={product} />

            {addToCartError && <AddToCartErrorMessage error={addToCartError} />}
          </form>
        )}

        <TariffPDPMessage
          product={productSelected}
          tariffSettings={tariffSettings}
        />

        {!hideProductValueProps && (
          <ProductValueProps
            productValueProps={product.fields.productValueProps}
          />
        )}
      </div>
    ),
    // TODO(lint): Fix this dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      productSelected,
      shopifyProductSelected,
      variant,
      handleCartFormSubmit,
      addToCartError,
      hideProductValueProps,
    ],
  );

  return (
    <div className="flex flex-col sm:px-6">
      <div className="flex items-center mb-2 md:mb-3">
        {productFields.previousTitle && (
          <span className="mr-4 text-h-x4 font-bold">
            {productFields.previousTitle}
          </span>
        )}

        {productFields.badge && (
          <div>
            <DynamicComponent component={productFields.badge} />
          </div>
        )}
      </div>
      <Heading
        tag={HeadingTag.H1}
        size={HeadingSize.H1}
        text={productFields.title}
        classes="mb-1.5"
      />

      {productFields.shortDescription && (
        <p className="mb-[9px]">{productFields.shortDescription}</p>
      )}

      {productFields.additionalDescription && (
        <p className="pt-3 mb-[9px]">{productFields.additionalDescription}</p>
      )}

      {variant && (
        <p className="mb-3 text-[1.3rem] font-bold md:text-2xl hidden">
          {productFields.precedingPriceText && (
            <span>{productFields.precedingPriceText}</span>
          )}
          {variant.formattedPrice}
        </p>
      )}

      {shouldUseYotpoReviews() && enableYotpoReviewsSummary && (
        <div className="mb-5 md:mb-6 mt-1 md:mt-2">
          <YotpoReviewStars product={product} />
        </div>
      )}

      {shouldUseTrustedShopsReviews() && enableTrustedShopsReviewsSummary && (
        <div className="mb-2">
          <TrustedShopReviews
            shopifyProduct={shopifyProduct}
            widgetType="ratings-short"
          />
        </div>
      )}

      {enableTabsFlag && alternativeProduct && shopifyAlternativeProduct ? (
        <ProductOptionsAddToCartTabs
          originalComponent={OriginalProductOptions}
          alternativeProduct={alternativeProduct}
          shopifyAlternativeProduct={shopifyAlternativeProduct}
          t={t}
          shipmentList={shipmentList}
          subscriptionPlanInstallments={subscriptionPlanInstallments}
          tariffSettings={tariffSettings}
          additionalSavingsText={additionalSavingsText}
          hideProductValueProps={hideProductValueProps}
        />
      ) : (
        OriginalProductOptions
      )}
    </div>
  );
};

export default ProductOptionsAddToCart;
